import React from 'react';
import { isEmpty, uniqBy } from 'lodash';
import { v4 as uuidV4 } from 'uuid';
import LabelIcon from '@atlaskit/icon/glyph/label';
import { COMPONENT_HIERARCHY_TYPES } from '../../../utils/consts';

// very simple className utility for creating a classname string...
// Falsy arguments are ignored:
// const active = true
// const className = classnames(
//    "class1",
//    !active && "class2",
//    active && "class3"
// ); // returns -> class1 class3";
//
export const classnames = (...classes) => {
    // Use Boolean constructor as a filter callback
    // Allows for loose type truthy/falsey checks
    // Boolean("") === false;
    // Boolean(false) === false;
    // Boolean(undefined) === false;
    // Boolean(null) === false;
    // Boolean(0) === false;
    // Boolean("classname") === true;
    return classes.filter(Boolean).join(' ');
};

export const arrayToTree = (array, parent, tree, withParentId = false) => {
    tree = typeof tree !== 'undefined' ? tree : [];
    parent = typeof parent !== 'undefined' ? parent : { id: null };

    let children;
    if (withParentId) {
        children = array.filter((child) => {
            return child.parentId === parent.id || child.parentId === parent.treeId;
        });
    } else {
        children = array.filter((child) => {
            return child.parentTreeId === parent.treeId;
        });
    }

    if (!isEmpty(children)) {
        if (parent.id === null) {
            tree = children;
        } else {
            parent.children = children;
        }
        children.forEach((child) => {
            arrayToTree(array, child, tree, withParentId);
        });
    }

    return tree;
};

export const arrayToTreeWithParentId = (array, parent, tree) => {
    return arrayToTree(array, parent, tree, true);
};

const getTitle = (component) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span>{component?.name}</span>
        <span
            style={{ color: '#7A869A', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: 'calc(100% - 48px)' }}
        >
            {component?.projects?.map((p) => p.jiraProjectName).join(',')}
        </span>
    </div>
);

export const componentsLabelsSelectTreeFlatData = (virtualLabels, virtualComponents) => {
    const finalTree = [];
    virtualLabels.forEach((item) => {
        const treeId = uuidV4();
        finalTree.push({
            ...item,
            value: treeId,
            treeId,
            selectable: false,
            title: (
                <span style={{ display: 'flex', cursor: 'default' }}>
                    <LabelIcon className="select-label-icon" /> {item.name}
                </span>
            ),
            type: COMPONENT_HIERARCHY_TYPES.LABEL,
            parentId: null,
            path: [item.id],
        });
    });
    virtualLabels.forEach((label) => {
        const componentsPerLabel = virtualComponents.filter((item) =>
            item.virtualLabels.some((lbl) => lbl.id === label.id),
        );
        const componentsWithNoParentLocally = componentsPerLabel.filter(
            (cp) =>
                cp.parents?.length === 0 ||
                !componentsPerLabel.some((cpLabel) => cp.parents?.map((p) => p.id).includes(cpLabel.id)),
        );
        componentsWithNoParentLocally.forEach((component) => {
            const treeId = uuidV4();
            const path = [label.id, treeId];
            const treeItem = {
                ...component,
                title: getTitle(component),
                selectedTitle: component.name,
                value: treeId,
                treeId,
                type: COMPONENT_HIERARCHY_TYPES.COMPONENT,
                parentId: label.id,
                path,
            };
            finalTree.push(treeItem);
            const addChildren = (currentParent) => {
                const children = componentsPerLabel.filter((vc) =>
                    vc.parents?.map((p) => p.id).includes(currentParent.id),
                );
                if (children.length) {
                    children.forEach((child) => {
                        const childTreeId = uuidV4();
                        const pathChild = [...currentParent.path, treeId];
                        const childToAdd = {
                            ...child,
                            value: treeId,
                            title: getTitle(child),
                            selectedTitle: child.name,
                            parentId: currentParent.treeId,
                            treeId: childTreeId,
                            type: COMPONENT_HIERARCHY_TYPES.COMPONENT,
                            path: pathChild,
                        };
                        finalTree.push(childToAdd);
                        addChildren(childToAdd);
                    });
                }
            };
            addChildren(treeItem);
        });
    });

    // Components with labels and parent
    // if don't share any labels with parent, the I need to insert parent in root with children
    const addToRoot = [];
    virtualComponents
        .filter((item) => item.virtualLabels.length && item.parents?.length > 0)
        .forEach((item) => {
            const parentVC = virtualComponents.find((vc) => vc.id === item.parentId);
            if (parentVC?.length) {
                const childLabelsEqualToParentLabels = item.virtualLabels.filter((itemLabel) =>
                    parentVC.virtualLabels.find((parentLabel) => parentLabel.id === itemLabel.id),
                );
                if (childLabelsEqualToParentLabels.length === 0) {
                    if (!addToRoot.some((vc) => vc.id === parentVC.id)) {
                        addToRoot.push({ ...parentVC, parentId: null });
                    }
                }
            }
        });

    // First add root items and its children
    const componentsWithNoLabelsAddedInTree = [];
    const componentsWithNoLabelsAndNoParent = uniqBy(
        virtualComponents.filter((item) => item.virtualLabels.length === 0).concat(addToRoot),
        (item) => item.id,
    ).filter((item) => !item.parents || item?.parents?.length === 0);

    componentsWithNoLabelsAndNoParent.forEach((component) => {
        const addChildren = (currentParent) => {
            const children = virtualComponents.filter((vc) => vc.parents?.map((p) => p.id).includes(currentParent.id));
            if (children.length) {
                children.forEach((child) => {
                    const treeId = uuidV4();
                    const path = [...currentParent.path, child.id];
                    const childToAdd = {
                        ...child,
                        value: treeId,
                        title: getTitle(child),
                        selectedTitle: child.name,
                        parentId: currentParent.treeId,
                        treeId,
                        type: COMPONENT_HIERARCHY_TYPES.COMPONENT,
                        path,
                    };
                    finalTree.push(childToAdd);
                    componentsWithNoLabelsAddedInTree.push(childToAdd);
                    addChildren(childToAdd);
                });
            }
        };

        const treeId = uuidV4();
        const path = [treeId];
        const componentInTree = {
            ...component,
            value: treeId,
            title: getTitle(component),
            selectedTitle: component.name,
            treeId,
            type: COMPONENT_HIERARCHY_TYPES.COMPONENT,
            parentId: null,
            path,
            virtualLabels: [],
        };
        finalTree.push(componentInTree);
        componentsWithNoLabelsAddedInTree.push(componentInTree);
        addChildren(componentInTree);
    });

    // Last ones are children not added before
    const componentsWithNoLabelsAndWithParent = uniqBy(
        virtualComponents.filter((item) => item.virtualLabels.length === 0).concat(addToRoot),
        (item) => item.id,
    ).filter((item) => item?.parents?.length > 0);

    componentsWithNoLabelsAndWithParent.forEach((component) => {
        const componentInTree = componentsWithNoLabelsAddedInTree.find((vc) => vc.id === component.id);
        if (!componentInTree) {
            let parentTreeId;
            const parentInTree = componentsWithNoLabelsAddedInTree.find((vc) =>
                component?.parents?.map((p) => p.id).includes(vc.id),
            );
            if (!parentInTree) {
                const parent = virtualComponents.find((vc) => vc.id === component.parentId);
                if (parent) {
                    const treeId = uuidV4();
                    const path = [treeId];
                    const parentInTreeToAdd = {
                        ...parent,
                        value: treeId,
                        title: getTitle(parent),
                        selectedTitle: parent.name,
                        treeId,
                        type: COMPONENT_HIERARCHY_TYPES.COMPONENT,
                        parentId: null,
                        path,
                        virtualLabels: [],
                    };
                    componentsWithNoLabelsAddedInTree.push(parentInTreeToAdd);
                    finalTree.push(parentInTreeToAdd);
                    parentTreeId = treeId;
                }
            } else {
                parentTreeId = parentInTree.treeId;
            }

            const treeId = uuidV4();
            const path = [treeId];
            const componentItemToAdd = {
                ...component,
                value: treeId,
                title: getTitle(component),
                selectedTitle: component.name,
                treeId,
                type: COMPONENT_HIERARCHY_TYPES.COMPONENT,
                parentId: parentTreeId,
                path,
                virtualLabels: [],
            };
            finalTree.push(componentItemToAdd);
            componentsWithNoLabelsAddedInTree.push(componentItemToAdd);
        }
    });
    return finalTree;
};

export const componentsLabelsSelectTree = (virtualLabels, virtualComponents) => {
    const finalTree = componentsLabelsSelectTreeFlatData(virtualLabels, virtualComponents);
    return arrayToTree(finalTree);
};

export const componentsHierarchyTree = (virtualComponents) => {
    const finalTree = [];

    virtualComponents
        .map((item) => ({
            ...item,
            value: `${item.name} ${item.id}`,
            title: item.name,
            type: COMPONENT_HIERARCHY_TYPES.COMPONENT,
        }))
        .forEach((item) => {
            finalTree.push(item);
        });

    return arrayToTree(finalTree);
};

export const componentsLabelsHierarchyTreeFlatData = (virtualLabels, allVirtualComponents, treeDataExpandedNodes) => {
    const finalTree = [];
    virtualLabels.forEach((label) => {
        const expanded = treeDataExpandedNodes?.[label.path?.toString()]
            ? treeDataExpandedNodes[label.path?.toString()]
            : false;
        finalTree.push({
            ...label,
            type: COMPONENT_HIERARCHY_TYPES.LABEL,
            expanded,
        });
    });

    allVirtualComponents.forEach((component) => {
        const expanded = treeDataExpandedNodes?.[component.path?.toString()]
            ? treeDataExpandedNodes[component.path?.toString()]
            : false;
        const componentInTree = {
            ...component,
            type: COMPONENT_HIERARCHY_TYPES.COMPONENT,
            virtualLabels: [],
            expanded,
        };
        finalTree.push(componentInTree);
    });
    return finalTree;
};

export const componentsLabelsHierarchyTree = (virtualLabels, virtualComponents) => {
    const finalTree = componentsLabelsHierarchyTreeFlatData(virtualLabels, virtualComponents);
    return arrayToTree(finalTree);
};

/**
 * Returns all component id's from a particular leaf of the tree, recursively
 * @param {*} component
 * @returns Array with component ids
 */
export const getAllComponentChildrenFrom = (component) => {
    const components = [];
    components.push(component);

    if (!component.children) {
        return components;
    }

    component.children.forEach((childComponent) => {
        const cps = getAllComponentChildrenFrom(childComponent);
        components.push(...cps);
    });
    return components;
};

/**
 * Returns all component id's from a particular leaf of the tree, recursively
 * @param {*} treeItem
 * @returns Array with component ids
 */
export const getAllComponentsChildrenFromTreeItem = (treeItem) => {
    const components = [];
    if (treeItem.type === COMPONENT_HIERARCHY_TYPES.COMPONENT) {
        components.push(treeItem);
    }

    if (!treeItem.children) {
        return components;
    }

    treeItem.children.forEach((child) => {
        const cps = getAllComponentsChildrenFromTreeItem(child);
        components.push(...cps);
    });
    return components;
};

/**
 * Checks how many times the same component appears in a tree path
 * @param {*} treeItem current tree item
 * @param {*} component component we need to check
 * @returns array with repetitions of the same component
 */
const getComponentInTree = (treeItem, component) => {
    const components = [];

    if (treeItem.id === component.id) {
        components.push(component);
    }

    if (!treeItem.children) {
        return components;
    }

    treeItem.children.forEach((childItem) => {
        const cps = getComponentInTree(childItem, component);
        components.push(...cps);
    });
    return components;
};

/**
 * Checks if the component has is present in other parts of the root tree. This would occur when this component
 * has multiple labels and some of these labels are under the same root label tree.
 * @param {*}
 * @returns true when finds same cp in other parts ofthe tree, false otherwise.
 */
export const hasSameComponentRootSubTree = (componentRootItem, node) => {
    const components = getComponentInTree(componentRootItem, node);
    return components.length > 1;
};

/**
 * @param {*}
 * @returns
 */
export const getComponentParentLabel = (prevPath, flatData) => {
    let parentLabel;

    prevPath.forEach((prevPathItem) => {
        const { node } = flatData.find((item) => item.node.treeId === prevPathItem);
        if (node.type === COMPONENT_HIERARCHY_TYPES.LABEL) {
            parentLabel = node;
        }
    });
    return parentLabel;
};

export const getParentLabelsPath = (nextPath, flatData) => {
    const labels = [];

    nextPath.forEach((nextPathItem) => {
        const { node } = flatData.find((item) => item.node.treeId === nextPathItem);
        if (node.type === COMPONENT_HIERARCHY_TYPES.LABEL) {
            labels.push(node);
        }
    });

    return labels;
};

export const isItemSelectedDescendant = (possibleDescendant, parent, flatTreeData) => {
    if (!parent) {
        return false;
    }

    let isSelectedDescendant = false;
    if (parent.id === possibleDescendant.id) {
        return true;
    }
    const possibleDescendantItemInTree = flatTreeData.filter((treeItem) => treeItem.id === possibleDescendant.id);
    possibleDescendantItemInTree.forEach((possibleDescendantItem) => {
        const isDescendant = (p) => {
            const children = flatTreeData.filter((treeItem) => treeItem.parentId === p.treeId);
            children.forEach((c) => {
                if (c.id === parent.id) {
                    isSelectedDescendant = true;
                } else {
                    isDescendant(c);
                }
            });
        };
        isDescendant(possibleDescendantItem);
    });

    return isSelectedDescendant;
};
